import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Download, Edit, Eye, FileText, FolderOpen, PencilLine, Plus, Share2, Trash2 } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import Modal from "../../components/ui/Modal";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import type {
  QuestionBankDraft,
  QuestionBankShareCandidate,
  QuestionChoiceLayout,
  QuestionTemplateType,
} from "../../types/question-bank";
import type { FileItem as StoredFileItem, MaarifTestFileMetadata } from "../../types/file";
import checkPermission from "../../utils/permission";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function QuestionBankListPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm, showToast } = useModal();
  const canViewQuestionBanks = checkPermission("question_bank_list");
  const canCreateQuestionBanks = checkPermission("question_bank_add");
  const canDeleteQuestionBanks = checkPermission("question_bank_delete");
  const canViewFiles = checkPermission("file_list");
  const [refreshKey, setRefreshKey] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [fileSelectOpen, setFileSelectOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState<QuestionBankDraft | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<QuestionBankDraft | null>(null);
  const [renameName, setRenameName] = useState("");
  const [shareSearch, setShareSearch] = useState("");
  const [shareCandidates, setShareCandidates] = useState<QuestionBankShareCandidate[]>([]);
  const [selectedShareCandidateId, setSelectedShareCandidateId] = useState("");
  const [allowSharedEditing, setAllowSharedEditing] = useState(false);
  const [loadingShareCandidates, setLoadingShareCandidates] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [maarifFiles, setMaarifFiles] = useState<StoredFileItem[]>([]);
  const [loadingMaarifFiles, setLoadingMaarifFiles] = useState(false);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [form, setForm] = useState({
    testAdi: "",
    kategori: "",
    soruSayisi: 10,
    settings: {
      visualMode: "standard" as "standard" | "image",
      choiceLayout: "vertical" as QuestionChoiceLayout,
      choiceCount: 5,
      showIntroText: false,
    },
  });

  const getCreateTemplateType = (): QuestionTemplateType =>
    form.settings.visualMode === "image"
      ? "image_with_caption"
      : form.settings.choiceLayout === "horizontal"
        ? "horizontal_choices"
        : "classic_vertical";

  const actions = useMemo(
    () =>
      canCreateQuestionBanks ? (
        <div className="flex items-center gap-2">
          {canViewFiles ? (
            <button
              onClick={() => setFileSelectOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FolderOpen className="h-4 w-4" />
              Dosyadan Aç
            </button>
          ) : null}
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Yeni Soru Oluştur
          </button>
        </div>
      ) : null,
    [canCreateQuestionBanks, canViewFiles]
  );

  const loadMaarifFiles = async () => {
    setLoadingMaarifFiles(true);
    try {
      const response = await ApiService.listFiles(1, 500);
      const files = (response.rows || []).filter((file) => {
        const metadata = file.metadata as MaarifTestFileMetadata | undefined;
        return metadata?.kind === "maarif_test" && metadata?.questionBankId;
      });
      setMaarifFiles(files);
    } catch (error: any) {
      await showAlert({
        title: "Dosyalar yüklenemedi",
        message: error?.message || "Kaydedilmiş Maarif testleri alınamadı.",
        type: "error",
      });
    } finally {
      setLoadingMaarifFiles(false);
    }
  };

  useEffect(() => {
    if (fileSelectOpen) {
      void loadMaarifFiles();
    }
  }, [fileSelectOpen]);

  useEffect(() => {
    if (!shareModalOpen) return;

    const timeoutId = window.setTimeout(async () => {
      setLoadingShareCandidates(true);
      try {
        const result = await ApiService.getQuestionBankShareCandidates({
          search: shareSearch,
          limit: 20,
        });
        setShareCandidates(result);
      } catch (error: any) {
        setShareCandidates([]);
        await showAlert({
          title: "Kullanıcılar yüklenemedi",
          message: error?.message || "Paylaşım adayları alınamadı.",
          type: "error",
        });
      } finally {
        setLoadingShareCandidates(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [shareModalOpen, shareSearch, showAlert]);

  const handleDownload = async (id: string) => {
    try {
      const { blob, fileName } = await ApiService.downloadQuestionBank(id);
      triggerBrowserDownload(blob, fileName || "question-bank-indesign.zip");
      showToast("Dizgici dosyası indirildi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "İndirme Hatası",
        message: error?.message || "Dosya indirilemedi.",
        type: "error",
      });
    }
  };

  const [pdfDownloadingId, setPdfDownloadingId] = useState<string | null>(null);

  const handleDownloadPdf = async (id: string) => {
    if (pdfDownloadingId) return;
    setPdfDownloadingId(id);
    try {
      showToast("Çözümlü test PDF'i hazırlanıyor...", "info");
      const { blob, fileName } = await ApiService.downloadQuestionBankPdf(id);
      triggerBrowserDownload(blob, fileName || "test_cozumlu.pdf");
      showToast("Test PDF'i indirildi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "PDF Hatası",
        message: error?.message || "PDF oluşturulamadı.",
        type: "error",
      });
    } finally {
      setPdfDownloadingId(null);
    }
  };

  const handleDelete = async (item: QuestionBankDraft) => {
    const confirmed = await showConfirm({
      title: "Taslağı Sil",
      message: `"${item.testAdi}" taslağını silmek istediğinize emin misiniz?`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      await ApiService.deleteQuestionBank(item.id);
      setRefreshKey((prev) => prev + 1);
      showToast("Taslak silindi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Silme Hatası",
        message: error?.message || "Taslak silinemedi.",
        type: "error",
      });
    }
  };

  const openShareModal = (item: QuestionBankDraft) => {
    setShareTarget(item);
    setShareSearch("");
    setShareCandidates([]);
    setSelectedShareCandidateId("");
    setAllowSharedEditing(false);
    setShareModalOpen(true);
  };

  const openRenameModal = (item: QuestionBankDraft) => {
    setRenameTarget(item);
    setRenameName(item.testAdi || "");
    setRenameModalOpen(true);
  };

  const closeRenameModal = () => {
    if (renaming) return;
    setRenameModalOpen(false);
    setRenameTarget(null);
    setRenameName("");
  };

  const handleRename = async () => {
    if (renaming || !renameTarget) return;

    const trimmedName = renameName.trim();
    if (!trimmedName) {
      await showAlert({ title: "Uyarı", message: "Test adı zorunludur.", type: "warning" });
      return;
    }

    if (trimmedName === renameTarget.testAdi) {
      closeRenameModal();
      return;
    }

    setRenaming(true);
    try {
      await ApiService.updateQuestionBank(renameTarget.id, {
        testAdi: trimmedName,
      });
      setRenameModalOpen(false);
      setRenameTarget(null);
      setRenameName("");
      setRefreshKey((prev) => prev + 1);
      showToast("Test adı güncellendi.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Güncelleme Hatası",
        message: error?.message || "Test adı güncellenemedi.",
        type: "error",
      });
    } finally {
      setRenaming(false);
    }
  };

  const handleShare = async () => {
    if (!shareTarget?.originalQuestionBankId || !selectedShareCandidateId) {
      await showAlert({
        title: "Uyarı",
        message: "Önce paylaşılacak kullanıcıyı seçin.",
        type: "warning",
      });
      return;
    }

    setSharing(true);
    try {
      await ApiService.shareQuestionBank(shareTarget.originalQuestionBankId, {
        recipientUserId: selectedShareCandidateId,
        allowEditing: allowSharedEditing,
      });
      setShareModalOpen(false);
      setRefreshKey((prev) => prev + 1);
      showToast("Dizgici kaydı paylaşıldı.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Paylaşım Hatası",
        message: error?.message || "Dizgici kaydı paylaşılamadı.",
        type: "error",
      });
    } finally {
      setSharing(false);
    }
  };

  const handleCreate = async () => {
    const trimmedName = form.testAdi.trim();
    const soruSayisi = Number(form.soruSayisi);

    if (!trimmedName) {
      await showAlert({ title: "Uyarı", message: "Test adı zorunludur.", type: "warning" });
      return;
    }

    if (!Number.isInteger(soruSayisi) || soruSayisi < 1 || soruSayisi > 200) {
      await showAlert({ title: "Uyarı", message: "Soru sayısı 1 ile 200 arasında olmalıdır.", type: "warning" });
      return;
    }

    setCreating(true);
    try {
      const draft = await ApiService.createQuestionBank({
        testAdi: trimmedName,
        kategori: form.kategori.trim(),
        soruSayisi,
        defaultQuestionSettings: {
          templateType: getCreateTemplateType(),
          choiceLayout: form.settings.choiceLayout,
          secenek_sayisi: form.settings.choiceCount,
          showIntroText: form.settings.visualMode === "image" ? form.settings.showIntroText : false,
        },
      });
      setCreateModalOpen(false);
      setForm({
        testAdi: "",
        kategori: "",
        soruSayisi: 10,
        settings: {
          visualMode: "standard",
          choiceLayout: "vertical",
          choiceCount: 5,
          showIntroText: false,
        },
      });
      navigate(`/question-bank-indesign/${draft.id}/edit`);
    } catch (error: any) {
      await showAlert({
        title: "Oluşturma Hatası",
        message: error?.message || "Taslak oluşturulamadı.",
        type: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const renderItem = (item: QuestionBankDraft) => (
    <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{item.testAdi}</h3>
              <div className="text-xs text-gray-500">
                {item.isShared ? `Paylaşılan • ${item.sharedBy?.name || "Bilinmeyen kullanıcı"}` : "Taslak kaydı"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm text-gray-600 md:grid-cols-2">
            <div className="inline-flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-gray-400" />
              {item.kategori || "Kategorisiz"}
            </div>
            <div className="inline-flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              {item.questionCount} soru
            </div>
            <div className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-gray-400" />
              Oluşturuldu: {formatDate(item.createdAt)}
            </div>
            <div className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-gray-400" />
              Güncellendi: {formatDate(item.updatedAt)}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
              item.isShared ? "border-sky-200 bg-sky-50 text-sky-700" : "border-amber-200 bg-amber-50 text-amber-700"
            }`}
          >
            {item.isShared ? "Paylaşılan" : "Taslak"}
          </span>
          {canCreateQuestionBanks ? (
            <button
              onClick={() => openRenameModal(item)}
              title="Test adını değiştir"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <PencilLine className="h-4 w-4" />
              Adı Değiştir
            </button>
          ) : null}
          <button
            onClick={() => navigate(`/question-bank-indesign/${item.id}/edit`)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Edit className="h-4 w-4" />
            Düzenle
          </button>
          <button
            onClick={() => handleDownload(item.id)}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
          >
            <Download className="h-4 w-4" />
            İndir
          </button>
          <button
            onClick={() => handleDownloadPdf(item.id)}
            disabled={pdfDownloadingId === item.id}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
            title="Çözümlü test PDF'i (sorular + cevap anahtarı + çözüm)"
          >
            <FileText className="h-4 w-4" />
            {pdfDownloadingId === item.id ? "Hazırlanıyor..." : "PDF"}
          </button>
          {!item.isShared && canCreateQuestionBanks ? (
            <button
              onClick={() => openShareModal(item)}
              className="inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-cyan-700 hover:bg-cyan-100"
            >
              <Share2 className="h-4 w-4" />
              Paylaş
            </button>
          ) : null}
          {!item.isShared && canDeleteQuestionBanks ? (
            <button
              onClick={() => handleDelete(item)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4" />
              Sil
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const tabs = useMemo<TabItemProps[]>(
    () => [
      {
        type: "list",
        title: "Tüm Taslaklar",
        path: "question-banks",
        tabId: "all-question-banks",
        isSearch: true,
        refreshKey,
        actions,
        emptyMessage: "Henüz oluşturulmuş bir Dizgici taslağı bulunmuyor.",
        fetcher: (page, searchTerm) =>
          ApiService.getQuestionBanks({
            page,
            limit: 20,
            search: searchTerm,
            sortBy: "updatedAt",
            sortOrder: "DESC",
          }),
        item: (data) => renderItem(data as QuestionBankDraft),
      },
    ],
    [actions, canCreateQuestionBanks, refreshKey]
  );

  return (
    <>
      {!canViewQuestionBanks ? (
        <div className="flex min-h-[calc(100vh-120px)] items-center justify-center px-6">
          <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
            <div className="text-lg font-semibold text-gray-900">Dizgici erişimi yok</div>
            <div className="mt-2 text-sm text-gray-600">Bu sayfayı görüntüleme yetkiniz bulunmuyor.</div>
          </div>
        </div>
      ) : (
        <>
          <TabPage title="Dizgici" tabs={tabs} />

          <Modal isOpen={createModalOpen && canCreateQuestionBanks} onClose={() => setCreateModalOpen(false)} title="Yeni Soru Oluştur" size="md">
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Test Adı</label>
                <input
                  value={form.testAdi}
                  onChange={(e) => setForm((prev) => ({ ...prev, testAdi: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Örn. TYT Matematik Deneme 1"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Kategori</label>
                <input
                  value={form.kategori}
                  onChange={(e) => setForm((prev) => ({ ...prev, kategori: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Opsiyonel"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Soru Sayısı</label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={form.soruSayisi}
                  onChange={(e) => setForm((prev) => ({ ...prev, soruSayisi: Number(e.target.value) }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
                <div className="text-sm font-semibold text-amber-950">Soru Düzeni</div>
                <div className="mt-1 mb-3 text-xs text-amber-900/70">Bu seçimler taslak açıldığında başlangıçtaki sorulara varsayılan olarak uygulanır.</div>

                <div className="space-y-4">
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900/80">Soru Gövdesi</div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, visualMode: "standard", showIntroText: false } }))}
                        className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                          form.settings.visualMode === "standard"
                            ? "border-blue-500 bg-white text-blue-900"
                            : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                        }`}
                      >
                        <div className="font-semibold">Standart</div>
                        <div className="mt-1 text-xs text-gray-500">Normal soru akışı ile başlasın.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, visualMode: "image" } }))}
                        className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                          form.settings.visualMode === "image"
                            ? "border-blue-500 bg-white text-blue-900"
                            : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                        }`}
                      >
                        <div className="font-semibold">Görselli</div>
                        <div className="mt-1 text-xs text-gray-500">Görsel alanı olan soru düzeni ile başlasın.</div>
                      </button>
                    </div>
                  </div>
                  {form.settings.visualMode === "image" ? (
                    <div>
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900/80">Üst Açıklama</div>
                      <div className="mb-2 text-xs text-amber-900/70">Sadece görselli sorular için kullanılır.</div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, showIntroText: false } }))}
                          className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                            !form.settings.showIntroText
                              ? "border-blue-500 bg-white text-blue-900"
                              : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                          }`}
                        >
                          <div className="font-semibold">Yok</div>
                          <div className="mt-1 text-xs text-gray-500">Görsel üstünde açıklama alanı açılmasın.</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, showIntroText: true } }))}
                          className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                            form.settings.showIntroText
                              ? "border-blue-500 bg-white text-blue-900"
                              : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                          }`}
                        >
                          <div className="font-semibold">Olsun</div>
                          <div className="mt-1 text-xs text-gray-500">Görselli sorularda üst açıklama alanı açık başlasın.</div>
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900/80">Şık Yerleşimi</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, choiceLayout: "vertical" } }))}
                        className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                          form.settings.choiceLayout === "vertical"
                            ? "border-blue-500 bg-white text-blue-900"
                            : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                        }`}
                      >
                        <div className="font-semibold">Alt Alta</div>
                        <div className="mt-1 text-xs text-gray-500">Klasik şık dizilimi.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, choiceLayout: "horizontal" } }))}
                        className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                          form.settings.choiceLayout === "horizontal"
                            ? "border-blue-500 bg-white text-blue-900"
                            : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                        }`}
                      >
                        <div className="font-semibold">Yatay</div>
                        <div className="mt-1 text-xs text-gray-500">Şıklar yan yana başlasın.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, choiceLayout: "staggered" } }))}
                        className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                          form.settings.choiceLayout === "staggered"
                            ? "border-blue-500 bg-white text-blue-900"
                            : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                        }`}
                      >
                        <div className="font-semibold">Çapraz</div>
                        <div className="mt-1 text-xs text-gray-500">Şıklar kademeli ve çapraz yerleşsin.</div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900/80">Şık Sayısı</div>
                    <div className="grid grid-cols-4 gap-2">
                      {[2, 3, 4, 5].map((count) => (
                        <button
                          key={count}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, settings: { ...prev.settings, choiceCount: count } }))}
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                            form.settings.choiceCount === count
                              ? "border-blue-500 bg-white text-blue-900"
                              : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setCreateModalOpen(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Vazgeç
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {creating ? "Oluşturuluyor..." : "Oluştur"}
                </button>
              </div>
            </div>
          </Modal>

          <Modal isOpen={renameModalOpen && canCreateQuestionBanks} onClose={closeRenameModal} title="Test Adını Değiştir" size="md">
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Mevcut Test Adı</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{renameTarget?.testAdi || "-"}</div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Yeni Test Adı</label>
                <input
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      void handleRename();
                    }
                  }}
                  disabled={renaming}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Örn. TYT Türkçe Deneme 1"
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeRenameModal}
                  disabled={renaming}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={handleRename}
                  disabled={renaming}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {renaming ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </Modal>

          <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Dizgiciyi Paylaş" size="md">
            <div className="space-y-4">
              <div className="rounded-xl border border-cyan-200 bg-cyan-50/70 p-4">
                <div className="text-sm font-semibold text-cyan-950">{shareTarget?.testAdi || "Dizgici kaydı"}</div>
                <div className="mt-1 text-xs text-cyan-900/70">Öğretmen departmanındaki kullanıcılar arasından seçim yapın.</div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Kullanıcı ara</label>
                <input
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Ad, soyad veya e-posta"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Paylaşılacak kişi</label>
                <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-2">
                  {loadingShareCandidates ? <div className="px-3 py-4 text-sm text-gray-500">Kullanıcılar yükleniyor...</div> : null}
                  {!loadingShareCandidates && shareCandidates.length === 0 ? <div className="px-3 py-4 text-sm text-gray-500">Eşleşen kullanıcı bulunamadı.</div> : null}
                  {!loadingShareCandidates
                    ? shareCandidates.map((candidate) => (
                        <label
                          key={candidate.id}
                          className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-sm transition ${
                            selectedShareCandidateId === candidate.id ? "border-cyan-400 bg-white" : "border-gray-200 bg-white hover:border-cyan-200"
                          }`}
                        >
                          <input
                            type="radio"
                            name="share_candidate"
                            checked={selectedShareCandidateId === candidate.id}
                            onChange={() => setSelectedShareCandidateId(candidate.id)}
                            className="mt-1"
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">{candidate.name}</div>
                            <div className="mt-1 text-xs text-gray-500">
                              {candidate.department?.name || "Departman yok"}
                              {candidate.email ? ` • ${candidate.email}` : ""}
                            </div>
                          </div>
                        </label>
                      ))
                    : null}
                </div>
              </div>

              <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3">
                <input
                  type="checkbox"
                  checked={allowSharedEditing}
                  onChange={(e) => setAllowSharedEditing(e.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Düzenleme yetkisi ver</div>
                  <div className="mt-1 text-xs text-gray-500">
                    Açık ise yapılan değişiklikler ana Dizgici kaydına yansır. Kapalı ise karşı tarafın değişiklikleri sadece kendi görünümünde kalır.
                  </div>
                </div>
              </label>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShareModalOpen(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  disabled={sharing || !selectedShareCandidateId}
                  className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-60"
                >
                  {sharing ? "Paylaşılıyor..." : "Paylaş"}
                </button>
              </div>
            </div>
          </Modal>

          <Modal isOpen={fileSelectOpen && canCreateQuestionBanks} onClose={() => setFileSelectOpen(false)} title="Dosyalardan Test Aç" size="lg">
            <div className="space-y-4">
              <div className="text-sm text-gray-600">Dosyalar modülüne kaydedilmiş Maarif testlerini doğrudan bağlı Dizgici taslağı olarak açabilirsiniz.</div>

              {loadingMaarifFiles ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-500">Kayıtlar yükleniyor...</div>
              ) : maarifFiles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500">
                  Dosyalar modülünde henüz kaydedilmiş bir Maarif test kaydı bulunmuyor.
                </div>
              ) : (
                <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                  {maarifFiles.map((file) => {
                    const metadata = file.metadata as MaarifTestFileMetadata;
                    return (
                      <button
                        key={file.id}
                        type="button"
                        onClick={() => {
                          setFileSelectOpen(false);
                          navigate(`/question-bank-indesign/${metadata.questionBankId}/edit`);
                        }}
                        className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-300 hover:bg-blue-50/40"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{file.originalName}</div>
                            <div className="mt-1 text-xs text-slate-500">
                              {metadata.lessonTitle || "Ders yok"} • {metadata.gradeLabel || "Sınıf yok"} • {metadata.questionCount || 0} soru
                            </div>
                            <div className="mt-2 text-xs text-slate-600">{metadata.topic || "Konu bilgisi bulunmuyor."}</div>
                          </div>
                          <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                            <Eye className="h-3.5 w-3.5" />
                            Aç
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Modal>
        </>
      )}
    </>
  );
}
